import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  CircularProgress,
  Link,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const PAGE_SIZE = 100;

const COLUMNS = [
  { key: 'name', label: 'Name', hasLink: true },
  { key: 'client', label: 'Client' },
  { key: 'community', label: 'Community' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'leadId', label: 'Lead ID' },
  { key: 'source', label: 'Source' },
  { key: 'stage', label: 'Stage' },
  { key: 'lastRespondedAt', label: 'Last Responded', sortable: true },
  { key: 'owner', label: 'Owner' },
  { key: 'lostReason', label: 'Lost Reason' },
  { key: 'createdAt', label: 'Created', sortable: true }
];

const formatDate = dateString => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

const formatPhone = phone => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const CopyableCell = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} arrow>
      <Box
        onClick={handleCopy}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': {
            color: theme =>
              theme.palette.mode === 'dark'
                ? theme.palette.primary.light
                : '#5267ff'
          }
        }}
      >
        <span>{value || '—'}</span>
        <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.4 }} />
      </Box>
    </Tooltip>
  );
};

const LeadsTable = ({
  data,
  totalCount,
  page,
  onPageChange,
  sortField,
  sortDirection,
  onSortChange,
  loading
}) => {
  if (loading || data === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: 0
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: 0
        }}
      >
        <Typography color="text.secondary">No leads found</Typography>
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        maxWidth: 'calc(100vw - 120px)',
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table
          stickyHeader
          size="small"
          sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              {COLUMNS.map((col, colIndex) => (
                <TableCell
                  key={col.key}
                  sx={{
                    backgroundColor: 'background.default',
                    color: theme =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '2px solid',
                    borderBottomColor: 'divider',
                    whiteSpace: 'nowrap',
                    ...(colIndex === 0 && {
                      minWidth: '200px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '4px',
                        transform: 'translateX(100%)',
                        background: theme =>
                          `linear-gradient(to right, ${
                            theme.palette.mode === 'dark'
                              ? 'rgba(0,0,0,0.45)'
                              : 'rgba(0,0,0,0.08)'
                          }, transparent)`
                      }
                    })
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortField === col.key}
                      direction={sortField === col.key ? sortDirection : 'asc'}
                      onClick={() => onSortChange(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={row.id || index}
                hover
                sx={{
                  backgroundColor: 'background.paper',
                  '&:hover': { backgroundColor: 'action.hover' },
                  '& td': {
                    borderBottom: theme =>
                      `1px solid ${theme.palette.divider} !important`
                  }
                }}
              >
                {COLUMNS.map((col, colIndex) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontSize: '14px',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      whiteSpace: 'nowrap',
                      ...(colIndex === 0 && {
                        fontWeight: 600,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: '4px',
                          transform: 'translateX(100%)',
                          background: theme =>
                            `linear-gradient(to right, ${
                              theme.palette.mode === 'dark'
                                ? 'rgba(0,0,0,0.45)'
                                : 'rgba(0,0,0,0.08)'
                            }, transparent)`
                        }
                      })
                    }}
                  >
                    {col.hasLink && row.mongoId ? (
                      <Link
                        href={`/inbox/searchLead/${row.mongoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                      >
                        {row[col.key] || '—'}
                      </Link>
                    ) : col.key === 'leadId' ? (
                      <CopyableCell value={row[col.key]} />
                    ) : col.key === 'createdAt' ||
                      col.key === 'lastRespondedAt' ? (
                      formatDate(row[col.key])
                    ) : col.key === 'phone' ? (
                      formatPhone(row[col.key])
                    ) : (
                      row[col.key] || '—'
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        sx={{ borderTop: '1px solid', borderTopColor: 'divider' }}
      />
    </Paper>
  );
};

export { PAGE_SIZE };
export default LeadsTable;
